import { useCallback, useEffect, useRef, useState } from "react";
import "./voice-recovery.css";
import { drawOrbRing, transcribeLive } from "./VoiceableInput";
import { notifyReadonlyBlocked, readonlyBlocksVoice } from "./readonly";
import {
  WALK_MAX_RECORDING_SECONDS,
  classifySendFailure,
  classifyStartFailure,
  createVoiceActivityGuard,
  voiceFailureKeepsRecording,
  voiceFailureMessageKey,
  voiceFailureRetryKey,
  worthTranscribing,
  type VoiceActivityGuard,
  type VoiceFailure
} from "./voice-recording";
import {
  dropVoiceDraft,
  readVoiceDraft,
  writeVoiceDraft,
  type VoiceDraftSurface
} from "./voice-draft";

// "Mluv a ukazuj" for delivered materials: the client turns on recording,
// points with the cursor over the document (or the sales page) and talks.
// We keep the audio transcript AND the pointer trail with the pointed-at
// snippets, so the partner and the build agent know exactly which passage
// each sentence belongs to. Same recorder pattern as the profile walk.
//
// F98 (Pavel 2026-08-07) rewired what happens when something goes wrong:
// a failure names its own cause, a recording that failed to DELIVER is held
// and re-sent rather than thrown away, leaving mid-recording warns, and a
// forgotten open microphone stops itself instead of buying a transcript of
// silence.

export interface WalkPoint { t: number; x: number; y: number; fact?: string }

const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];

type WalkPhase = "idle" | "recording" | "uploading" | "transcribing" | "sent" | "failed" | "resume";

interface HeldNote {
  blob: Blob;
  type: string;
  transcript: string | null;
  attachmentId: string | null;
}

export function walkTransferStatusKey(phase: string): string | null {
  if (phase === "uploading") return "status.uploading";
  if (phase === "transcribing") return "status.transcribing";
  if (phase === "sent") return "status.done";
  return null;
}

export function WalkFeedback(props: {
  csrfToken: string;
  t: (key: string) => string;
  // Locale key prefix: the client speaks to their partner (sales.walk), the
  // partner speaks to Alex (staffConsole.walk) - same recorder, own copy.
  copyPrefix?: string;
  // The capture surface calls pointerRef.current on pointer moves; the
  // recorder only listens while a recording is running.
  pointerRef: React.MutableRefObject<((point: Omit<WalkPoint, "t">) => void) | null>;
  onSubmit: (payload: { text: string | null; audioAttachmentId: string | null; pointerTrace: WalkPoint[] }) => Promise<boolean>;
  // Lets the host show a pointer-capture layer only while recording (needed
  // over iframes, which swallow pointer events).
  onRecordingChange?: (recording: boolean) => void;
  // Compact row for tight panels: small orb, one-line copy, auto-reset after
  // a saved note (the mic itself is the "record another" affordance).
  compact?: boolean;
  // Where an unfinished recording is parked so it survives the tab closing.
  // Keyed by the MATERIAL, not by the deliverable version, so a new version
  // published meanwhile still finds the note the client dictated.
  draft?: { surface: VoiceDraftSurface; targetId: string };
}) {
  const [phase, setPhase] = useState<WalkPhase>("idle");
  const walkKey = (suffix: string) => `${props.copyPrefix ?? "sales.walk"}.${suffix}`;
  const [elapsed, setElapsed] = useState(0);
  const [failure, setFailure] = useState<VoiceFailure | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const cancelledRef = useRef(false);
  const startingRef = useRef(false);
  const heldRef = useRef<HeldNote | null>(null);
  const traceRef = useRef<WalkPoint[]>([]);
  const heldTraceRef = useRef<WalkPoint[]>([]);
  const startedAtRef = useRef(0);
  const lastSampleRef = useRef(0);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const guardRef = useRef<VoiceActivityGuard | null>(null);
  const ringRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef(0);
  const draft = props.draft;

  useEffect(() => {
    let active = true;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const animate = (time: number) => {
      if (!active) return;
      let bins: Uint8Array | null = null;
      if (phase === "recording" && analyserRef.current) {
        const data = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(data);
        bins = data;
      }
      if (ringRef.current) drawOrbRing(ringRef.current, { bins, tick: reduceMotion ? 0 : time, level: 0.4, live: phase === "recording" });
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { active = false; cancelAnimationFrame(rafRef.current); };
  }, [phase]);

  // One second tick: the visible timer, the silence brake and the hard length
  // ceiling all read the same sample, so they can never disagree.
  useEffect(() => {
    if (phase !== "recording") return;
    const timer = window.setInterval(() => {
      const guard = guardRef.current;
      const verdict = guard ? guard.sample(Date.now()) : "continue";
      setElapsed(guard ? guard.elapsedSeconds() : 0);
      if (verdict === "silence_stop") {
        setNotice(props.t("voice.failure.silenceStopped"));
        // No speech at all in the take: cancel it, so nothing is paid for.
        stopRecording(!guard?.hadVoice());
      } else if (verdict === "limit_stop") {
        setNotice(props.t("voice.failure.limitStopped"));
        stopRecording(false);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [phase, props.t]);

  // Leaving mid-recording loses the note, and so does leaving while one is
  // held unsent. Both get the browser's confirm prompt (Pavel 2026-08-07).
  useEffect(() => {
    const atRisk = phase === "recording" || heldRef.current !== null;
    if (!atRisk) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [phase]);

  useEffect(() => {
    if (!props.compact || phase !== "sent") return;
    const timer = window.setTimeout(() => setPhase("idle"), 2500);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, props.compact]);

  // A leftover from a closed tab or a crashed browser is OFFERED, never
  // re-sent behind the client's back.
  useEffect(() => {
    if (!draft) return;
    let live = true;
    void readVoiceDraft(draft.surface, draft.targetId).then((record) => {
      if (!live || !record || heldRef.current) return;
      heldRef.current = {
        blob: new Blob(record.chunks, { type: record.mime }),
        type: record.mime,
        transcript: record.transcript,
        attachmentId: null
      };
      heldTraceRef.current = [];
      setPhase("resume");
    });
    return () => { live = false; };
  }, [draft?.surface, draft?.targetId]);

  const onPointer = useCallback((point: Omit<WalkPoint, "t">) => {
    if (recorderRef.current?.state !== "recording") return;
    const now = performance.now();
    if (now - lastSampleRef.current < 120 || traceRef.current.length >= 5500) return;
    lastSampleRef.current = now;
    traceRef.current.push({ t: Math.round(now - startedAtRef.current), ...point });
  }, []);

  useEffect(() => {
    const ref = props.pointerRef;
    if (phase === "recording") ref.current = onPointer;
    else if (ref.current === onPointer) ref.current = null;
    props.onRecordingChange?.(phase === "recording");
    return () => { if (ref.current === onPointer) ref.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, onPointer, props.pointerRef]);

  function teardown() {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
    analyserRef.current = null;
    guardRef.current = null;
    void audioContextRef.current?.close().catch(() => undefined);
    audioContextRef.current = null;
  }

  function fail(reason: VoiceFailure) {
    setFailure(reason);
    setPhase("failed");
  }

  function persistDraft(state: "recording" | "unsent", mime: string, chunks: Blob[], transcript: string | null) {
    if (!draft || chunks.length === 0) return;
    const now = Date.now();
    void writeVoiceDraft({
      id: `${draft.surface}:${draft.targetId}`,
      surface: draft.surface,
      targetId: draft.targetId,
      mime,
      chunks,
      state,
      transcript,
      startedAt: startedAtRef.current || now,
      updatedAt: now
    });
  }

  function forgetDraft() {
    heldRef.current = null;
    if (draft) void dropVoiceDraft(draft.surface, draft.targetId);
  }

  async function start() {
    // Impersonation preview: refuse before any recording effort is invested.
    if (readonlyBlocksVoice()) {
      notifyReadonlyBlocked();
      return;
    }
    // A second click while the first getUserMedia is still in flight makes the
    // browser hand out two streams and one of them loses the device.
    if (startingRef.current || recorderRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      fail("unsupported");
      return;
    }
    startingRef.current = true;
    setNotice(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MIME_CANDIDATES.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ?? "";
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      traceRef.current = [];
      cancelledRef.current = false;
      startedAtRef.current = performance.now();
      lastSampleRef.current = 0;
      forgetDraft();
      setFailure(null);
      setElapsed(0);
      let analyser: AnalyserNode | null = null;
      try {
        const context = new AudioContext();
        analyser = context.createAnalyser();
        analyser.fftSize = 256;
        context.createMediaStreamSource(stream).connect(analyser);
        audioContextRef.current = context;
        analyserRef.current = analyser;
      } catch { /* visual only, and the guard fails open without it */ }
      guardRef.current = createVoiceActivityGuard({
        analyser,
        maxSeconds: WALK_MAX_RECORDING_SECONDS,
        startedAt: Date.now()
      });
      recorder.ondataavailable = (event) => {
        if (event.data.size === 0) return;
        chunksRef.current.push(event.data);
        // Written as it arrives: a crash costs at most this one second.
        persistDraft("recording", recorder.mimeType || mime || event.data.type, chunksRef.current, null);
      };
      recorder.onstop = () => {
        const chunks = chunksRef.current;
        const type = recorder.mimeType || mime || chunks[0]?.type || "audio/webm";
        const hadVoice = guardRef.current?.hadVoice() ?? true;
        teardown();
        if (cancelledRef.current || chunks.length === 0) {
          forgetDraft();
          setPhase("idle");
          return;
        }
        const blob = new Blob(chunks, { type });
        // Never buy a transcript of silence (Pavel 2026-08-07).
        if (!worthTranscribing({ hadVoice, byteSize: blob.size })) {
          forgetDraft();
          fail("empty");
          return;
        }
        heldRef.current = { blob, type, transcript: null, attachmentId: null };
        heldTraceRef.current = traceRef.current.slice();
        persistDraft("unsent", type, chunks, null);
        void send();
      };
      recorder.start(1000);
      setPhase("recording");
    } catch (error) {
      teardown();
      fail(classifyStartFailure(error));
    } finally {
      startingRef.current = false;
    }
  }

  function stopRecording(cancelled: boolean) {
    cancelledRef.current = cancelled;
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
  }

  // Delivery of the HELD note. Every step that already succeeded is kept, so a
  // retry never pays Scribe twice and never re-uploads the same archive.
  async function send() {
    const held = heldRef.current;
    if (!held) return;
    setFailure(null);
    try {
      if (!held.transcript) {
        setPhase("uploading");
        const text = await transcribeLive(held.blob, held.type, props.csrfToken, {
          onUploadComplete: () => setPhase("transcribing")
        });
        // No speech in the recording (transcript is empty after cleaning):
        // saving would create an unreadable note - say so honestly instead.
        if (!text.trim()) {
          forgetDraft();
          fail("empty");
          return;
        }
        held.transcript = text;
        persistDraft("unsent", held.type, [held.blob], text);
      }
      setPhase("transcribing");
      if (!held.attachmentId) {
        try {
          const form = new FormData();
          form.set("scope", "feedback_audio");
          // 🔴 The transcript rides WITH the archive. Without it the Worker
          // stores the take as `pending` and the queue pays Scribe a SECOND
          // time for audio we have already transcribed live (VoiceableInput
          // has always sent it; the two walk recorders never did).
          if (held.transcript) form.set("transcript", held.transcript);
          form.set("file", new File([held.blob], `doc-walk.${held.type.includes("mp4") ? "m4a" : "webm"}`, { type: held.type }));
          const upload = await fetch("/api/attachments", {
            method: "POST",
            credentials: "include",
            headers: { "X-Launchpad-CSRF": props.csrfToken },
            body: form
          });
          const uploaded = await upload.json() as { attachmentId?: string };
          if (upload.ok && uploaded.attachmentId) held.attachmentId = uploaded.attachmentId;
        } catch { /* archive optional: the transcript is the note */ }
      }
      const delivered = await props.onSubmit({
        text: held.transcript,
        audioAttachmentId: held.attachmentId,
        pointerTrace: heldTraceRef.current
      });
      if (!delivered) throw new Error("feedback_failed");
      forgetDraft();
      heldTraceRef.current = [];
      setPhase("sent");
    } catch (error) {
      // The recording is still ours. Say what failed and offer to send again -
      // unless the reason says there is nothing worth keeping (no speech), in
      // which case the take is dropped so it is never offered, or paid for,
      // a second time.
      const reason = classifySendFailure(error, navigator.onLine !== false);
      if (!voiceFailureKeepsRecording(reason)) forgetDraft();
      fail(reason);
    }
  }

  const holdsNote = heldRef.current !== null;
  const retryKey = failure ? voiceFailureRetryKey(failure) : "voice.failure.retryRecord";
  const retryHeld = Boolean(failure && voiceFailureKeepsRecording(failure) && holdsNote);

  return (
    <div className={`profile-walk doc-walk${props.compact ? " compact" : ""} ${phase === "recording" ? "recording" : ""}`}>
      <div className="profile-walk-bar" role="region" aria-label={props.t(walkKey("title"))}>
        <button
          type="button"
          className={`voice-inline-mic profile-walk-orb ${phase === "recording" ? "recording" : ""}`}
          aria-label={phase === "recording" ? props.t(walkKey("stop")) : props.t(walkKey("start"))}
          title={phase === "recording" ? props.t(walkKey("stop")) : props.t(walkKey("start"))}
          disabled={phase === "uploading" || phase === "transcribing"}
          onClick={() => {
            if (phase === "recording") stopRecording(false);
            else if (phase !== "uploading" && phase !== "transcribing") void start();
          }}
        >
          <canvas ref={ringRef} className="voice-orb-ring" aria-hidden="true" />
          {/* The orb IS the control (Pavel 24.7., finding 16): while it
              records it grows, breathes with the voice and carries a stop
              square - clicking it stops AND saves. No duplicate button. */}
          {phase === "recording" ? (
            <span className="voice-orb-stop" aria-hidden="true" />
          ) : (
            <svg className="voice-orb-mic" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path fill="currentColor" d="M12 14.5a3.3 3.3 0 0 0 3.3-3.3V6.3a3.3 3.3 0 1 0-6.6 0v4.9a3.3 3.3 0 0 0 3.3 3.3Zm5.8-3.3a5.8 5.8 0 0 1-11.6 0H4.4a7.6 7.6 0 0 0 6.7 7.5v2.5h1.8v-2.5a7.6 7.6 0 0 0 6.7-7.5h-1.8Z" />
            </svg>
          )}
        </button>
        <div className="profile-walk-copy">
          {phase === "recording" ? (
            <>
              <p className="walk-rec-line">
                <span className="walk-rec-dot" aria-hidden="true" />
                <span className="profile-walk-timer">{formatTime(elapsed)}</span>
                <span className="walk-rec-instruction">{props.t(walkKey("recInstruction"))}</span>
              </p>
              <button className="quiet-link walk-rec-cancel" type="button" onClick={() => stopRecording(true)}>
                {props.t(walkKey("cancel"))}
              </button>
            </>
          ) : phase === "sent" ? (
            <>
              {!props.compact && (
                <div className="profile-walk-actions">
                  <button className="secondary-button compact-button" type="button" onClick={() => void start()}>{props.t(walkKey("another"))}</button>
                </div>
              )}
            </>
          ) : phase === "uploading" || phase === "transcribing" ? (
            <span aria-hidden="true" />
          ) : phase === "resume" ? (
            <div className="profile-walk-actions walk-resume" role="status">
              <strong>{props.t("voice.failure.resumeTitle")}</strong>
              <p>{props.t("voice.failure.resumeBody")}</p>
              <div className="walk-resume-actions">
                <button className="primary-button compact-button" type="button" onClick={() => void send()}>
                  {props.t("voice.failure.resumeSend")}
                </button>
                <button className="quiet-link" type="button" onClick={() => { forgetDraft(); setPhase("idle"); }}>
                  {props.t("voice.failure.resumeDiscard")}
                </button>
              </div>
            </div>
          ) : (
            <>
              <strong title={props.compact ? props.t(walkKey("explain")) : undefined}>{props.t(walkKey("title"))}</strong>
              {!props.compact && <p>{props.t(walkKey("explain"))}</p>}
              {phase === "failed" && failure && (
                <div className="profile-walk-actions">
                  <p className="message error-message" role="alert">{props.t(voiceFailureMessageKey(failure))}</p>
                  {retryHeld && <p className="walk-held-note">{props.t("voice.failure.heldNote")}</p>}
                  <button
                    className="secondary-button compact-button"
                    type="button"
                    onClick={() => (retryHeld ? void send() : void start())}
                  >
                    {props.t(retryKey)}
                  </button>
                </div>
              )}
            </>
          )}
          {notice && phase !== "recording" && <p className="walk-transfer-notice" role="status">{notice}</p>}
        </div>
      </div>
      <p className="walk-transfer-status" role="status" aria-live="polite">
        {walkTransferStatusKey(phase) ? props.t(walkKey(walkTransferStatusKey(phase)!)) : "\u00a0"}
      </p>
    </div>
  );
}

function formatTime(total: number): string {
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}
